// components/ui/PageSkeleton.jsx
import React from "react";

/**
 * @component PageSkeleton
 * @param {Object} props
 * @param {boolean} [props.hasTabs=true] - Show tabs or not
 * @param {boolean} [props.hasButton=true] - Show button or not
 * @param {boolean} [props.hasSearch=true] - Show search bar
 * @param {boolean} [props.hasSortOptions=true] - Show sort options
 * @param {number} [props.sortOptionCount=3] - Number of sort placeholders
 * @param {number} [props.tableColumnCount=5] - Number of columns in table
 * @param {number} [props.tableRowCount=5] - Number of table rows
 */
const PageSkeletons = ({ tableColumnCount = 7, tableRowCount = 8 }) => {
  return (
    <div className="bg-gray-100 flex flex-col h-[calc(100vh-80px)]">
      <div className="flex-1 p-6 bg-white flex flex-col h-full">
        {/* Header (Two sections: left and right) */}
        <div className={`h-4 mb-3 w-[159px] bg-[#E7E7E7] rounded`}></div>
        <div className="shrink-0 sticky top-0 z-10 bg-white pb-4">
          {/* Left and Right Sections */}
          <div className="flex items-center justify-between mb-4">
            {/* Left Section */}
            <div className={`h-8 w-[159px] bg-[#E7E7E7] rounded`}></div>

            {/* Right Section */}
            <div className="h-4 w-[147px] right-0 bg-[#E7E7E7] rounded"></div>
          </div>

          {/* Tabs + Button */}
          <div className="flex space-x-4  ml-3">
            <>
              <div className="h-[14px] w-[75px] bg-[#E7E7E7] rounded"></div>
              <div className="h-[14px] w-[75px] bg-[#E7E7E7] rounded"></div>
            </>
          </div>
          <div className="flex items-center justify-between border-b border-[#E7E7E7] pb-3 mb-3"></div>
        </div>

        {/* Table Section */}
        <div className="flex-1 overflow-y-auto border border-[#D7D9DB] rounded-md">
          {/* Table Header */}
          <div className="flex border-b border-[#D7D9DB] bg-gray-50 py-3">
            {[...Array(tableColumnCount)].map((_, i) => (
              <div key={`col-${i}`} className="px-4 flex-1">
                <div className="h-[14px] w-[31px] bg-[#E0E0E0] rounded max-w-[200px]"></div>
              </div>
            ))}
            <div className="px-4 w-32">
              {/* <div className="h-4 bg-gray-200 rounded w-5/6"></div> */}
            </div>
          </div>

          {/* Table Rows with fixed height */}
          {[...Array(tableRowCount)].map((_, rowIdx) => (
            <div
              key={rowIdx}
              className="flex items-center h-12 border-b border-[#D7D9DB]"
            >
              {[...Array(tableColumnCount)].map((_, i) => (
                <div key={`row-${rowIdx}-col-${i}`} className="px-4 flex-1">
                  <div className="h-[16px] w-[56px] bg-[#F9F9F9] rounded max-w-[200px]"></div>
                </div>
              ))}
              <div className="px-4 w-32 flex items-center">
                <div className="h-[32px] w-[73px] border-2 bg-[#F6F6F6] border-[#E0E0E0] rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Sidebar navigation skeleton
 * @component SidebarSkeleton
 * @param {boolean} isOpen - Whether sidebar is expanded
 * @param {number} menuCount - Number of menu items to render
 * @param {number} overviewCount - Number of overview items to render
 * @param {number} settingsCount - Number of settings items to render
 * @returns {JSX.Element} Animated sidebar navigation skeleton
 */

const SidebarSkeleton = ({
  isOpen,
  menuCount,
  overviewCount,
  settingsCount,
}) => {
  const renderSkeletonItem = () => (
    <li className="animate-pulse">
      <div className="flex items-center p-2 rounded group">
        <span className="bg-[#F9F9F9] h-6 w-6 mr-2 rounded"></span>
        {isOpen && (
          <span className="bg-[#E7E7E7] h-[14px] w-[75px] rounded"></span>
        )}
      </div>
    </li>
  );

  return (
    <>
      {Array.from({ length: menuCount }).map((_, index) => (
        <React.Fragment key={`menu-item-${index}`}>
          {renderSkeletonItem()}
        </React.Fragment>
      ))}

      {menuCount > 0 && overviewCount > 0 && (
        <hr className="border-[#D7D9DB] my-2" />
      )}

      {Array.from({ length: overviewCount }).map((_, index) => (
        <React.Fragment key={`overview-item-${index}`}>
          {renderSkeletonItem()}
        </React.Fragment>
      ))}

      {(overviewCount > 0 || settingsCount > 0) && (
        <hr className="border-[#D7D9DB] my-2" />
      )}

      {settingsCount > 0 && renderSkeletonItem()}
    </>
  );
};

/**
 * Header skeleton with user dropdown placeholder
 * @component HeaderSkeleton
 * @returns {JSX.Element} Header bar skeleton with user profile placeholder
 */
const HeaderSkeleton = () => {
  return (
    <header className="bg-white p-4 flex items-center border-b border-[#B7B7B7] w-full h-[72px] animate-pulse">
      {/* Sidebar Button */}

      <div className="flex-grow" />

      {/* User Dropdown Skeleton */}
      <div className="flex items-center border w-[139px] h-[48px] border-[#C2C6CA] p-1 rounded gap-2">
        <div className="w-[32px] h-[32px] rounded-full bg-[#E7E7E7]" />
        <div className="flex flex-col space-y-1">
          <div className="w-[87px] h-[14px] bg-[#E7E7E7] rounded" />
          <div className="w-[87px] h-[7px] bg-[#F9F9F9] rounded" />
        </div>
      </div>
    </header>
  );
};

/**
 * Simple loading spinner
 * @component Spinner
 * @returns {JSX.Element} Circular spinning loader
 */
let Spinner = ({ width = "w-8", height = "h-8", border="border-4", className }) => {
  return (
    <div className="flex justify-center items-center h-full">
      <div
        className={`${width} ${height} ${border} border-[#1C40CA] border-t-transparent rounded-full  animate-spin ${className}`}
      ></div>
    </div>
  );
};

export { SidebarSkeleton, HeaderSkeleton, PageSkeletons, Spinner };
